import { RelayEnvironment } from "@/RelayEnvironment";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { useEffect, useState } from "react";
import { fetchQuery, graphql, useMutation } from "react-relay";
import type { afterReciveInviteChatQuery$data as ChatQueryType } from './__generated__/afterReciveInviteChatQuery.graphql'
import type { afterReciveInviteStartChatMutation as StartChatMutation } from './__generated__/afterReciveInviteStartChatMutation.graphql'
import { RequestStatus } from "@/defines/requestDefines";
import { useUserStore } from "@/defines/store/userStore";
import { useNavigate } from 'react-router-dom';

const AfterReciveInviteChatQuery = graphql`
query afterReciveInviteChatQuery($linkparameter: String!){
  insurance_request_log_connection(where: {request_ai_consult_details: {linkparameter: {_eq: $linkparameter}}}) {
    edges {
      node {
        request_ai_consult_details {
          id
          linkparameter
          startchatdate
          created_at
        }
        requeststatus
        requesttype
        recipientid
        insuranceid
        id
        created_at
        caredetailid
        aiconsultdetailid
      }
    }
  }
}
`

const AfterReciveInviteStartChatMutation = graphql`
mutation afterReciveInviteStartChatMutation(
    $requeststatus: Int!
    $recipientid: String!
    $linkparameter: String!
  ) {
    update_insurance_request_log(where: {request_ai_consult_details: {linkparameter: {_eq: $linkparameter}}}, 
    _set: {recipientid: $recipientid, requeststatus: $requeststatus}) {
        affected_rows
        }
    update_request_ai_consult_detail(where: {linkparameter: {_eq: $linkparameter}}, _set: {startchatdate: "now()"}) {
        affected_rows
    }
  }
`

export default function AfterReciveInvite() {
    const [title, setTitle] = useState('')
    const [errorMsg, setErrorMsg] = useState('')
    const [commitStartChat] = useMutation<StartChatMutation>(AfterReciveInviteStartChatMutation);

    const search = window.location.search
    const params = new URLSearchParams(search)
    const type = params.get('type') // determine the logic
    const token = params.get('token')

    const navigate = useNavigate();
    const userId = useUserStore.getState().userData.id;

    useEffect(() => {
        if (!userId) {
            setErrorMsg("請先登入後再點擊此連結 ! ");
            return;
        }
        if (!token) {
            setErrorMsg("無法取得對應的資料 ! 請確認連結是否有遺漏資料 ! ");
            return;
        }

        try {

            switch (type) {
                case "insurance-org": // will not exist because clerk will do this part
                    break;
                case "ai-consult":
                    setTitle('正在處理AI醫師資料...正在查詢');

                    fetchQuery(RelayEnvironment, AfterReciveInviteChatQuery, {
                        linkparameter: token
                    })
                        .subscribe({
                            next: (response) => {
                                console.log('response', response);
                                var resData = response as ChatQueryType;
                                if (resData.insurance_request_log_connection?.edges?.length == 0) {
                                    setErrorMsg("無法取得對應的資料 ! 請確認連結是否有遺漏資料 ! ");
                                    return;
                                }
                                var data = resData.insurance_request_log_connection?.edges[0].node;
                                if (data.recipientid) {
                                    setErrorMsg("此連結已經被使用 ! ");
                                    return;
                                }
                                if (data.requeststatus !== RequestStatus.init) {
                                    setErrorMsg("此連結已經失效 ! ");
                                    return;
                                }

                                setTitle('正在處理AI醫師資料...正在更新');
                                commitStartChat({
                                    variables: {
                                        recipientid: userId,
                                        requeststatus: RequestStatus.chatUsing,
                                        linkparameter: token,
                                    },
                                    onCompleted: () => {
                                        console.log('onCompleted');
                                        setTitle('成功取得聊天使用權限 ! 請開始您的諮詢 ! (3秒後自動轉跳)');
                                        setTimeout(() => {
                                            navigate('/patient/ai-consult');
                                        }, 3000)

                                    },
                                    onError: (error) => {
                                        console.error('Error updating user', error);
                                    }
                                });
                            },
                            error: (err: any) => {
                                setErrorMsg("資料處理錯誤 ! 請檢查連結再嘗試一次。");
                                console.log('err', err);
                            },
                        });

                    break;
                default:
                    setErrorMsg("無法取得對應的資料 ! 請確認連結是否有遺漏資料 ! ");
                    break;
            }
        } catch (ex) {
            setErrorMsg("資料處理錯誤 ! 請檢查連結再嘗試一次。");
            console.log('ex', ex);
        }

    }, [])

    return (
        <>
            {
                errorMsg ?
                    /* error */
                    < Dialog open={true} onClose={() => { }} className="relative z-50" >
                        <DialogBackdrop
                            transition
                            className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
                        />

                        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                                <DialogPanel
                                    transition
                                    className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-sm sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
                                >
                                    <div>
                                        <div className="mt-3 text-center sm:mt-5">
                                            <DialogTitle as="h3" className="text-2xl mt-5 font-semibold text-gray-900">
                                                {errorMsg}
                                            </DialogTitle>
                                        </div>
                                    </div>
                                    <div className="mt-5 sm:mt-6">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                navigate('/');
                                            }}
                                            className="inline-flex w-full justify-center rounded-md bg-red-400 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
                                        >
                                            返回首頁
                                        </button>
                                    </div>
                                </DialogPanel>
                            </div>
                        </div>
                    </Dialog >
                    :

                    /* processing */
                    <Dialog open={true} onClose={() => { }} className="relative z-50">
                        <DialogBackdrop
                            transition
                            className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
                        />

                        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                                <DialogPanel
                                    transition
                                    className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-sm sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
                                >
                                    <div>
                                        <div className="mt-3 text-center sm:mt-5">
                                            <div className="mt-2">
                                                <div className="flex items-center justify-center w-full h-full">
                                                    <span className="w-32 h-32 border-t-2 border-b-2 border-gray-900 rounded-full animate-spin"></span>
                                                </div>
                                            </div>
                                            <DialogTitle as="h3" className="text-2xl mt-5 font-semibold text-gray-900">
                                                {title}
                                            </DialogTitle>
                                        </div>
                                    </div>
                                </DialogPanel>
                            </div>
                        </div>
                    </Dialog >
            }

        </>
    )
}